import {
  Paper,
  Text,
  TextInput,
  Textarea,
  Button,
  Group,
  Container,
  Select,
  SimpleGrid,
  createStyles,
  LoadingOverlay,
  rem,
} from '@mantine/core';
import {ContactIconsList} from '../contactIcons/ContactIcons';
import {useAppStore} from '../../store';
import {useForm} from '@mantine/form';
import {toast} from 'react-hot-toast';
import {ContactInterface} from '../../models';
import bg from '../../assets/img/blue-bg.jpg';

const useStyles = createStyles(theme => {
  const BREAKPOINT = theme.fn.smallerThan('sm');

  return {
    wrapper: {
      display: 'flex',
      backgroundColor:
        theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white,
      borderRadius: theme.radius.lg,
      padding: rem(4),
      border: `${rem(1)} solid ${
        theme.colorScheme === 'dark'
          ? theme.colors.dark[8]
          : theme.colors.gray[2]
      }`,

      [BREAKPOINT]: {
        flexDirection: 'column',
      },
    },

    form: {
      boxSizing: 'border-box',
      flex: 1,
      padding: theme.spacing.xl,
      paddingLeft: `calc(${theme.spacing.xl} * 2)`,
      borderLeft: 0,

      [BREAKPOINT]: {
        padding: theme.spacing.md,
        paddingLeft: theme.spacing.md,
      },
    },

    fields: {
      marginTop: rem(-12),
    },

    fieldInput: {
      flex: 1,

      '& + &': {
        marginLeft: theme.spacing.md,

        [BREAKPOINT]: {
          marginLeft: 0,
          marginTop: theme.spacing.md,
        },
      },
    },

    fieldsGroup: {
      display: 'flex',

      [BREAKPOINT]: {
        flexDirection: 'column',
      },
    },

    contacts: {
      boxSizing: 'border-box',
      position: 'relative',
      borderRadius: theme.radius.lg,
      backgroundImage: `url(${bg})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      border: `${rem(1)} solid transparent`,
      padding: theme.spacing.xl,
      flex: `0 0 ${rem(280)}`,

      [BREAKPOINT]: {
        marginBottom: theme.spacing.sm,
        paddingLeft: theme.spacing.md,
      },
    },

    title: {
      marginBottom: `calc(${theme.spacing.xl} * 1.5)`,
      fontFamily: `Greycliff CF, ${theme.fontFamily}`,

      [BREAKPOINT]: {
        marginBottom: theme.spacing.xl,
      },
    },

    control: {
      backgroundColor: '#21485e',
      [BREAKPOINT]: {
        flex: 1,
      },
    },
  };
});

function ContactForm() {
  const {classes} = useStyles();
  const {contactStore} = useAppStore();

  const {loading, error} = contactStore.getters;

  const form = useForm({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      enquiry_title: '',
      message: '',
    },
    // functions will be used to validate values at corresponding key
    validate: {
      name: value =>
        value.length < 2 ? 'Name must have at least 2 letters' : null,
      email: value => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      phone: value =>
        value.length < 10 ? ' Phone must have at least 10 numbers' : null,
      message: value => (value.length < 10 ? 'Message is too short' : null),
      enquiry_title: value =>
        value.length < 1 ? 'This field is required' : null,
    },
  });

  const sendMessage = async (formData: ContactInterface) => {
    await contactStore.actions.createContact(formData);
    if (error) {
      toast.error(error);
    } else {
      form.reset();
      toast.success('Message sent');
    }
  };

  return (
    <Container>
      <Paper shadow="md" radius="lg">
        <div className={classes.wrapper}>
          <div className={classes.contacts}>
            <Text fz="lg" fw={700} className={classes.title} c="#fff">
              Contact information
            </Text>

            <ContactIconsList variant="white" />
          </div>

          {loading ? (
            <LoadingOverlay
              loaderProps={{size: 'sm', color: 'green', variant: 'dots'}}
              overlayOpacity={0.3}
              overlayColor="#c5c5c5"
              visible
            />
          ) : (
            <form
              className={classes.form}
              onSubmit={form.onSubmit(values => sendMessage(values))}
            >
              <Text fz="lg" fw={700} className={classes.title}>
                Get in touch
              </Text>

              <div className={classes.fields}>
                <SimpleGrid cols={2} breakpoints={[{maxWidth: 'sm', cols: 1}]}>
                  <TextInput
                    label="Your name"
                    placeholder="Your name"
                    required
                    withAsterisk
                    {...form.getInputProps('name')}
                  />
                  <TextInput
                    label="Your email"
                    placeholder="hello@mantine.dev"
                    required
                    withAsterisk
                    {...form.getInputProps('email')}
                  />
                </SimpleGrid>
                <Select
                  size="xl"
                  radius="md"
                  label="My enquiry is about"
                  required
                  withAsterisk
                  placeholder="Please select..."
                  data={[
                    {value: 'request_callback', label: 'Request a call back'},
                    {value: 'book_appointment', label: 'Book an appointment'},
                    {value: 'request_quote', label: 'Request a quote'},
                    {value: 'recruitment_enquiry', label: 'Recruitment'},
                  ]}
                  {...form.getInputProps('enquiry_title')}
                  styles={theme => ({
                    item: {
                      // applies styles to selected item
                      '&[data-selected]': {
                        '&, &:hover': {
                          backgroundColor: '#485e21',
                          color: '#fff',
                        },
                      },

                      // applies styles to hovered item (with mouse or keyboard)
                      '&[data-hovered]': {},
                    },
                    label: {
                      //color: '#485e21',
                    },
                    input: {},
                  })}
                />

                <TextInput
                  label="Telephone/Mobile"
                  placeholder="Phone"
                  min={10}
                  max={12}
                  withAsterisk
                  mt="md"
                  radius="md"
                  styles={theme => ({
                    label: {
                      //color: '#485e21',
                    },
                    input: {
                      borderColor: '#485e21',
                      backgroundColor: '#485e21',
                    },
                  })}
                  {...form.getInputProps('phone')}
                />

                <Textarea
                  mt="md"
                  withAsterisk
                  label="Tell us more about your enquiry"
                  placeholder="Tell us more about your enquiry"
                  required
                  minRows={3}
                  {...form.getInputProps('message')}
                />

                <Group position="right" mt="md">
                  <Button type="submit" className={classes.control}>
                    Send message
                  </Button>
                </Group>
                <p className="mt-4">
                  This site is protected by reCAPTCHA and the Google Privacy
                  Policy and Terms of Service apply.
                </p>
              </div>
            </form>
          )}
        </div>
      </Paper>
    </Container>
  );
}

export default ContactForm;
